"use server";

import { headers } from "next/headers";
import { saveQuoteRequest } from "@/lib/cms";

// ─── Simple in-memory rate limiter ───────────────────────────────────────────
// Allows max 5 submissions per IP per 10 minutes.
// For production with multiple servers, replace with Redis/Upstash.
const rateLimitMap = new Map<string, { count: number; resetAt: number }>();

function checkRateLimit(ip: string): boolean {
  const now = Date.now();
  const windowMs = 10 * 60 * 1000; // 10 minutes
  const maxRequests = 5;

  const entry = rateLimitMap.get(ip);
  if (!entry || now > entry.resetAt) {
    rateLimitMap.set(ip, { count: 1, resetAt: now + windowMs });
    return true; // allowed
  }
  if (entry.count >= maxRequests) {
    return false; // blocked
  }
  entry.count += 1;
  return true; // allowed
}

// ─── Input validation ─────────────────────────────────────────────────────────
function validateInput(data: {
  name: string;
  email: string;
  description: string;
  subject?: string;
}): string | null {
  if (!data.name || data.name.trim().length === 0) return "Name is required";
  if (data.name.trim().length > 100) return "Name must be under 100 characters";

  if (!data.email || data.email.trim().length === 0) return "Email is required";
  if (data.email.length > 254) return "Email address is too long";
  // Basic email format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(data.email.trim())) return "Invalid email address";

  if (!data.description || data.description.trim().length === 0)
    return "Project details are required";
  if (data.description.trim().length < 10)
    return "Please provide more detail (at least 10 characters)";
  if (data.description.trim().length > 2000)
    return "Description must be under 2000 characters";

  if (data.subject && data.subject.length > 200)
    return "Subject must be under 200 characters";

  return null; // valid
}

// ─── Server Action ─────────────────────────────────────────────────────────────
export async function submitQuoteAction(formData: {
  name: string;
  email: string;
  description: string;
  subject?: string;
}) {
  // 1. Get caller IP for rate limiting
  const headersList = await headers();
  const ip =
    headersList.get("x-forwarded-for")?.split(",")[0].trim() ??
    headersList.get("x-real-ip") ??
    "unknown";

  // 2. Rate limit check
  if (!checkRateLimit(ip)) {
    return {
      success: false,
      error: "Too many requests. Please wait 10 minutes and try again.",
    };
  }

  // 3. Validate and sanitize inputs
  const validationError = validateInput(formData);
  if (validationError) {
    return { success: false, error: validationError };
  }

  // 4. Sanitize: trim all strings before saving
  const sanitized = {
    name: formData.name.trim(),
    email: formData.email.trim().toLowerCase(),
    description: formData.description.trim(),
    subject: formData.subject?.trim(),
  };

  // 5. Save to CMS
  try {
    return await saveQuoteRequest(sanitized);
  } catch (error) {
    console.error("Server Action Error:", error);
    return { success: false, error: "Failed to submit request" };
  }
}
